// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import { computeValidatePassword } from '@Helpers/password';
import useValidateForm from '@Hooks/useValidateForm';
import { RulesViewerPassword } from '@Molecules/RulesPassword/RulesPassword';
import { AuthEventList } from '@Organisms/AccountDetails/eventList';
import RouteBuilder from '@Services/RouteBuilder';
import { Button, Input } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PasswordPolicyOutput } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';

import './LoginPasswordChange.scss';
import { THForm } from '@Molecules/THForm/THForm';

interface LoginPasswordChangeProps {
    eventList: AuthEventList;
    secret: string;
}

export const LoginPasswordChange = ({ eventList, secret }: LoginPasswordChangeProps): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const { form, isValid, onFormValuesChange } = useValidateForm();
    const [passwordRule, setPasswordRule] = useState<PasswordPolicyOutput>();
    const [errorRule, setErrorRule] = useState<string[]>([]);
    const [active, setActive] = useState<boolean>(false);

    const validator = useCallback(
        (str) => computeValidatePassword(passwordRule as PasswordPolicyOutput)(str),
        [passwordRule],
    );

    const onFinish = (values: Store): void => {
        const { password, confirmPassword } = values || {};
        if (validator(password) && password === confirmPassword) {
            eventList
                .changePassword({ password, secret })
                .then(() => {
                    history.push(RouteBuilder.compute('login.default'));
                })
                .catch(() => {
                    history.push(RouteBuilder.compute('login.reset'));
                });
        }
    };

    useEffect(() => {
        if (eventList) {
            eventList.onGetPasswordPolicy().then((data) => {
                setPasswordRule(data);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventList]);

    return (
        <div className="login-password-change">
            <div className="login-password-change__title-container">
                <div className="login-password-change__title">{i('login.change.title')}</div>
                <div className="login-password-change__subtitle">
                    {i('login.change.reset')} <br />
                    {i('login.change.enter')}
                </div>
            </div>

            <div className="login-password-change__form">
                <THForm
                    name="changeForm"
                    onValuesChange={(changedValue: Store, allValues: Store): void => {
                        if (allValues['password']) {
                            setActive(true);
                        }

                        if (passwordRule && passwordRule.enabled && allValues['password']) {
                            setErrorRule(validator(allValues['password']));
                        }
                        if ('confirmPassword' in changedValue) {
                            if (changedValue['confirmPassword'] !== allValues['password']) {
                            }
                        }
                        onFormValuesChange();
                    }}
                    onFinish={onFinish}
                    form={form}
                >
                    <THForm.Item
                        name="password"
                        help={
                            passwordRule?.enabled ? (
                                <RulesViewerPassword local={i} rules={passwordRule} error={errorRule} active={active} />
                            ) : null
                        }
                        rules={[
                            {
                                required: true,
                                message: i('login.form.password.required'),
                            },
                        ]}
                    >
                        <Input.Password
                            className="login-password-change__item"
                            placeholder={i('login.form.password.placeholder')}
                            prefix={<ICONS.LockIcon className="icon" />}
                        />
                    </THForm.Item>

                    <THForm.Item
                        name="confirmPassword"
                        rules={[
                            {
                                required: true,
                                message: i('login.form.confirm-password.required'),
                            },
                            ({ getFieldValue }) => ({
                                validator: (_, value): Promise<void> => {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(i('login.form.confirm-password.same')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            className="login-password-change__item"
                            placeholder={i('login.form.confirm-password.placeholder')}
                            prefix={<ICONS.LockIcon className="icon" />}
                            type="password"
                        />
                    </THForm.Item>
                </THForm>

                <div className="login-password-change__actions">
                    <Button type="primary" disabled={!isValid} onClick={(): void => form.submit()}>
                        {i('login.set-password')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
